// ─── Flujo piloto (CT → PV → RQ → OC → RP → RS), fase 1: el seguimiento ─────
// Un flujo alterno al de siempre, copiado del panel de ventas, que se prueba en
// paralelo antes de volverlo el principal. Arma la cadena de cada pedido con
// los documentos que ya existen (core/cadenaPedido.js). Lo único que crea es la
// requisición (fase 2) y, desde «Lo que falta por ordenar», las OCP y las
// cuentas por pagar de siempre, por sus puertas de siempre (core/requisicion.js). El pedido (PV) es la misma OP; la OC es la OCP, la
// RP su recepción, la FP su causación, la RS la remisión y la FV la factura.
//
// Dos pantallas:
//   · «Pedidos (PV)»: cada pedido con su estado derivado y qué sigue; al
//     abrirlo, la cadena completa renglón por renglón y sus servicios.
//   · «Material en transición»: lo que falta por entregar de todas las órdenes
//     vigentes, partido en bodega · comprado · pedido a compras · sin pedir.
// Las ven los administradores y los asesores (rol Vendedor, ROLE_PAGES) mientras se prueba.
import { getCostoPromedio, getStockDisponible, getStockProducto } from "../core/accounting.js";
import { sellarAutor } from "../core/autoria.js";
import { ESTADOS_PEDIDO, POR_GESTIONAR, cadenaDeOP, materialEnTransicion, resumenPedido } from "../core/cadenaPedido.js";
import { saveData, siguienteNumeroDoc } from "../core/cloudSync.js";
import { C } from "../core/config.js";
import { desgloseIVA } from "../core/iva.js";
import { armarRS, preparacionRS, remisionesEnBorrador, remisionesPorFacturar } from "../core/despacho.js";
import { componentesDeArticulo, stockDisponibleKit } from "../core/kit.js";
import { valorRemision } from "../core/remisionValor.js";
import { apartadoTotalFn, apartadosDeOP, avisosPendientes, cruceOCP, loQueLlego, repartirRecepcion } from "../core/recepcion.js";
import { agruparOrdenes, faltantesParaRQ, lineasPorOrdenar, rqPendienteFn, rqsDeOP, saldoLinea } from "../core/requisicion.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, fmtDate, formatMiles, genId, normalize, parseNumber, searchBox, today } from "../core/utils.js";
import { registerActions } from "../ui/actions.js";
import { render } from "../ui/app.js";
import { sincronizarCxpDirectasOP } from "./cuentasPagar.js";
import { aplicarIngresoOCP, esOcpInformativa } from "./cxp.js";
import { getConvertedToList, renderDocFormModal, sincronizarOcpDeCostos } from "./documentos.js";
import { planDesdeRemisiones, showFacturarPlan } from "./dataico.js";

const AZUL = "#2B4A82";

export function ctxPiloto() {
  const d = state.data;
  const ctx = {
    ocp: d.ocp || [], remisiones: d.remisiones || [], facturas: d.facturas || [],
    cuentas_pagar: d.cuentas_pagar || [], cuentas_cobro: d.cuentas_cobro || [], cotizaciones: d.cotizaciones || [],
    requisiciones: d.requisiciones || [], recepciones: d.recepciones || [], apartados: d.apartados || [],
    stock: (a) => getStockProducto(a),
    componentes: (op, a) => componentesDeArticulo(op, a),
    costo: (a) => getCostoPromedio(a)
  };
  // Lo pedido a compras y no ordenado todavía: la tercera de las cuatro partes.
  ctx.rqPendiente = rqPendienteFn(ctx);
  // Lo apartado para cada pedido (fase 3): va primero en el reparto de la bodega.
  ctx.apartadoTotal = apartadoTotalFn(ctx);
  return ctx;
}

// Todo se calcula de una vez: el reparto de la bodega necesita ver todas las
// órdenes vigentes juntas para que las cuatro partes sumen exacto.
function calcular() {
  const ctx = ctxPiloto();
  const { cadenas, filas } = materialEnTransicion(state.data.pedidos || [], ctx);
  const hoy = today();
  const porId = new Map(cadenas.map(c => [c.op.id, c]));
  const pedidos = (state.data.pedidos || []).map(op => {
    const cad = porId.get(op.id) || cadenaDeOP(op, ctx);
    return { cad, res: resumenPedido(cad, filas, hoy, avisosPendientes(op.id, ctx)) };
  });
  return { pedidos, filas, ctx };
}

const n = (v) => formatMiles(Math.round(v * 100) / 100);
const chip = (estado) => {
  const e = ESTADOS_PEDIDO[estado] || ESTADOS_PEDIDO.por_remisionar;
  return `<span style="background:${e.bg};color:${e.color};padding:3px 9px;border-radius:6px;font-size:10.5px;font-weight:700;white-space:nowrap;">${e.label}</span>`;
};
const enlace = (tipo, doc, txt) => doc && doc.id
  ? `<span data-action="ir-a-documento" data-tipo="${tipo}" data-id="${escapeHTML(doc.id)}" style="color:#0F766E;font-weight:700;cursor:pointer;text-decoration:underline;white-space:nowrap;">${escapeHTML(txt || doc.numero || "—")}</span>`
  : `<span style="color:${C.muted};">${escapeHTML(txt || "—")}</span>`;
const barra = (hecho, total, color) => {
  const pct = total > 0 ? Math.min(100, Math.round(hecho / total * 100)) : 0;
  return `<div style="height:6px;background:#e2e8f0;border-radius:3px;overflow:hidden;margin-top:3px;"><div style="width:${pct}%;height:100%;background:${color};"></div></div>`;
};
const avisoPiloto = `
  <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:8px;padding:8px 12px;margin-bottom:10px;font-size:11px;color:#5b21b6;">
    🧪 <b>Flujo piloto</b> — copia del panel de ventas (CT → PV → RQ → OC → RP → RS), en prueba al lado del flujo de siempre.
    Lee los documentos que ya existen; lo único que crea son las requisiciones y, desde «Lo que falta por ordenar», las órdenes de compra y cuentas por pagar, por la misma puerta de siempre.
  </div>`;

const FILTROS = [
  ["por_gestionar", "Por gestionar"],
  ["por_remisionar", "Por remisionar"],
  ["parcial", "Remisionados en parte"],
  ["por_facturar", "Por facturar"],
  ["facturado", "Facturados"],
  ["todos", "Todos"]
];
const pasa = (estado, f) => f === "todos" || (f === "por_gestionar" ? POR_GESTIONAR.includes(estado) : estado === f);

// ─── Pedidos (PV) ───────────────────────────────────────────────────────────
export function renderPilotoPedidos() {
  if (!state.piloto) state.piloto = { filtro: "por_gestionar" };
  const { pedidos, filas: filasT, ctx } = calcular();
  if (state.piloto.pedidoId) {
    const p = pedidos.find(x => x.cad.op.id === state.piloto.pedidoId);
    if (p) return renderDetalle(p, filasT, ctx);
    state.piloto.pedidoId = null;
  }
  const filtro = state.piloto.filtro || "por_gestionar";
  const q = normalize(state.searches.piloto || "");
  const clientes = state.data.clientes || [];
  const nombreCliente = (id) => (clientes.find(c => c.id === id) || {}).nombre || "—";
  const busca = (p) => !q || [p.cad.op.numero, nombreCliente(p.cad.op.cliente_id), p.cad.op.numero_oc_cliente, p.cad.docs.ct && p.cad.docs.ct.numero]
    .some(v => normalize(v || "").includes(q));
  const visibles = pedidos.filter(busca);
  const cuenta = {};
  visibles.forEach(p => { cuenta[p.res.estado] = (cuenta[p.res.estado] || 0) + 1; });
  cuenta.por_gestionar = visibles.filter(p => POR_GESTIONAR.includes(p.res.estado)).length;
  cuenta.todos = visibles.length;
  const lista = visibles.filter(p => pasa(p.res.estado, filtro))
    .sort((a, b) => (b.cad.op.fecha || "").localeCompare(a.cad.op.fecha || "") || String(b.cad.op.numero || "").localeCompare(String(a.cad.op.numero || "")));

  const filas = lista.map(({ cad, res }) => {
    const op = cad.op, d = cad.docs;
    const ocps = d.oficiales.map(o => enlace("OCP", o)).concat(d.informativas.map(o => enlace("OCP", o)));
    return `
      <tr data-piloto-pedido="${escapeHTML(op.id)}" style="border-top:1px solid ${C.border};background:white;vertical-align:top;">
        <td style="padding:8px 10px;white-space:nowrap;">
          <div style="font-weight:800;color:${AZUL};font-size:12.5px;">${escapeHTML(op.numero || "")}</div>
          <div style="font-size:10.5px;color:${C.muted};">${fmtDate(op.fecha)}</div>
          ${d.ct ? `<div style="font-size:10px;">de ${enlace("CT", d.ct._noEsta ? null : d.ct, d.ct.numero)}</div>` : ""}
        </td>
        <td style="padding:8px 10px;min-width:150px;">
          <div style="font-weight:700;color:${C.text};font-size:11.5px;">${escapeHTML(nombreCliente(op.cliente_id))}</div>
          ${op.numero_oc_cliente ? `<div style="font-size:10px;color:${C.muted};">OC cliente: ${escapeHTML(op.numero_oc_cliente)}</div>` : ""}
          <div style="font-size:10px;color:${C.muted};">${fmtCOP(op.total)}</div>
        </td>
        <td style="padding:8px 10px;min-width:120px;">
          <div style="font-size:11px;"><b>${n(res.despachada)}</b> / ${n(res.pedida)} uds</div>
          ${barra(res.despachada, res.pedida, "#b45309")}
          <div style="font-size:9.5px;color:${C.muted};margin-top:2px;">${d.despachos.map(rc => enlace("RC", rc)).join(" ") || "sin remisiones"}</div>
        </td>
        <td style="padding:8px 10px;min-width:110px;font-size:10px;">${ocps.join(" ") || `<span style="color:${C.muted};">sin compras</span>`}</td>
        <td style="padding:8px 10px;min-width:100px;font-size:10px;">${d.facturas.map(f => enlace("FACT", f)).join(" ") || `<span style="color:${C.muted};">sin factura</span>`}</td>
        <td style="padding:8px 10px;text-align:center;">${chip(res.estado)}${res.avisos.length && POR_GESTIONAR.includes(res.estado) ? `<div style="font-size:9.5px;color:#047857;font-weight:700;margin-top:3px;">📦 llegó material</div>` : ""}${res.atrasado ? `<div style="font-size:9.5px;color:#991b1b;font-weight:700;margin-top:3px;">⏰ atrasado</div>` : ""}</td>
        <td data-piloto-que="${escapeHTML(op.id)}" style="padding:8px 10px;min-width:210px;font-size:10.5px;color:${C.text};">${res.queHacer.map(t => `<div>${escapeHTML(t)}</div>`).join("")}</td>
        <td style="padding:8px 6px;text-align:right;"><button data-action="piloto-abrir" data-id="${escapeHTML(op.id)}" style="${btnStyle(AZUL, null, true)}">Ver cadena</button></td>
      </tr>`;
  }).join("");

  return `
    <div>
      <h2 style="margin:0;font-size:17px;font-weight:800;color:${AZUL};">📑 Pedidos (PV)</h2>
      <div style="font-size:11.5px;color:${C.muted};margin:2px 0 10px;">Cada pedido por su cadena: qué falta comprar, qué llegó, qué se remisionó y qué se facturó. El estado no se escribe: sale de las cantidades de cada renglón.</div>
      ${avisoPiloto}
      ${searchBox("search-piloto", state.searches.piloto || "", "Buscar por pedido, cliente, OC del cliente o cotización…", AZUL)}
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;">
        ${FILTROS.map(([id, label]) => `
          <button data-action="piloto-filtro" data-f="${id}" data-piloto-filtro="${id}"
            style="padding:5px 11px;border-radius:14px;border:1px solid ${filtro === id ? AZUL : C.border};background:${filtro === id ? AZUL : "white"};color:${filtro === id ? "white" : C.text};font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;">
            ${label} <span style="opacity:.75;">${cuenta[id] || 0}</span>
          </button>`).join("")}
      </div>
      ${lista.length === 0
        ? `<div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};font-size:12.5px;">${filtro === "por_gestionar" ? "🎉 No hay pedidos por gestionar." : "Ningún pedido en este filtro."}</div>`
        : `<div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;">
            <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
              <thead><tr style="background:#f8fafc;">
                ${["Pedido", "Cliente", "Despacho (RS)", "Compras (OC)", "Facturas (FV)", "Estado", "Qué sigue", ""].map(h => `<th style="padding:7px 10px;text-align:left;font-size:10.5px;color:${C.muted};">${h}</th>`).join("")}
              </tr></thead>
              <tbody>${filas}</tbody>
            </table>
          </div>`}
    </div>`;
}

// ─── El detalle: la cadena del pedido renglón por renglón ───────────────────
function renderDetalle({ cad, res }, filas, ctx) {
  const op = cad.op, d = cad.docs;
  const cli = (state.data.clientes || []).find(c => c.id === op.cliente_id);
  const mias = filas.filter(f => f.cad.op.id === op.id);
  const deLinea = (l, art) => mias.find(f => f.linea.idx === l.idx && f.c.articulo === art);

  const paso = (titulo, docs, tipo) => `
    <div style="background:white;border:1px solid ${C.border};border-radius:8px;padding:7px 10px;min-width:120px;">
      <div style="font-size:9.5px;color:${C.muted};font-weight:700;letter-spacing:.4px;">${titulo}</div>
      <div style="font-size:11px;margin-top:2px;">${docs.length ? docs.map(x => enlace(tipo, x.id ? x : null, x.numero)).join(" ") : `<span style="color:${C.muted};">—</span>`}</div>
    </div>`;
  const flecha = `<div style="color:${C.muted};font-size:14px;align-self:center;">→</div>`;

  const celdaCompra = (c) => {
    const docs = c.ocps.map(o => `<div>${enlace("OCP", o)} <span style="color:${C.muted};">${n(o.recibido)}/${n(o.cantidad)} · ${escapeHTML(o.estado || "")}</span></div>`).join("");
    return docs || `<span style="color:${C.muted};">—</span>`;
  };
  const partes = (f) => !f ? `<span style="color:${C.muted};">—</span>` : [
    f.apartado ? `<div style="color:#0f766e;font-weight:700;">📌 ${n(f.apartado)} apartados</div>` : "",
    f.apartadoSinStock ? `<div style="color:#b91c1c;">⚠️ ${n(f.apartadoSinStock)} apartados que ya no están en bodega</div>` : "",
    f.enBodega - f.apartado + f.apartadoSinStock > 0 ? `<div style="color:#047857;">📦 ${n(f.enBodega - f.apartado + f.apartadoSinStock)} en bodega sin apartar</div>` : "",
    f.comprado ? `<div style="color:#1d4ed8;">🚚 ${n(f.comprado)} en camino</div>` : "",
    f.enRQ ? `<div style="color:#7c3aed;">📝 ${n(f.enRQ)} pedido a compras</div>` : "",
    f.sinPedir ? `<div style="color:#b91c1c;font-weight:700;">🚨 ${n(f.sinPedir)} sin pedir</div>` : ""
  ].join("") || `<span style="color:#047857;">✓</span>`;

  const filasLineas = cad.lineas.map(l => {
    const principal = !l.esKit ? l.aComprar[0] : null;
    const f = principal ? deLinea(l, principal.articulo) : null;
    const filaKit = l.esKit ? l.aComprar.map(c => {
      const fc = deLinea(l, c.articulo);
      return `
        <tr style="background:#fafafa;border-top:1px dashed ${C.border};font-size:10.5px;">
          <td></td>
          <td style="padding:4px 8px;padding-left:20px;color:${C.muted};">↳ ${escapeHTML(c.articulo)} <span>(${n(c.porUnidad)} por unidad)</span></td>
          <td style="padding:4px 8px;text-align:right;">${n(c.necesita)} faltan</td>
          <td style="padding:4px 8px;">${celdaCompra(c)}</td>
          <td style="padding:4px 8px;text-align:right;">${n(c.recibido)}</td>
          <td></td><td></td>
          <td style="padding:4px 8px;">${partes(fc)}</td>
        </tr>`;
    }).join("") : "";
    return `
      <tr data-piloto-linea="${l.idx}" style="border-top:1px solid ${C.border};vertical-align:top;">
        <td style="padding:6px 8px;color:${C.muted};">${l.idx + 1}</td>
        <td style="padding:6px 8px;min-width:170px;">
          <div style="font-weight:700;color:${C.text};">${escapeHTML(l.articulo || "—")}</div>
          ${l.esKit ? `<div style="font-size:9.5px;color:#7c3aed;font-weight:700;">kit: se compra por partes</div>` : ""}
          <div style="font-size:9.5px;color:${C.muted};">${fmtCOP(l.precio)} c/u</div>
        </td>
        <td style="padding:6px 8px;text-align:right;font-weight:700;">${n(l.pedida)}</td>
        <td style="padding:6px 8px;font-size:10.5px;">${principal ? celdaCompra(principal) : `<span style="color:${C.muted};">ver partes ↓</span>`}</td>
        <td style="padding:6px 8px;text-align:right;">${principal ? n(principal.recibido) : "—"}</td>
        <td style="padding:6px 8px;text-align:right;font-size:10.5px;"><b>${n(l.despachada)}</b>${l.remisiones.length ? `<div>${l.remisiones.map(r => enlace("RC", r)).join(" ")}</div>` : ""}</td>
        <td style="padding:6px 8px;text-align:right;font-size:10.5px;"><b>${n(l.facturada)}</b>${l.facturasL.length ? `<div>${l.facturasL.map(x => enlace("FACT", x)).join(" ")}</div>` : ""}</td>
        <td style="padding:6px 8px;font-size:10.5px;min-width:130px;">${l.debe <= 0 ? `<span style="color:#047857;font-weight:700;">✓ entregado</span>` : principal ? partes(f) : `<span style="color:${C.muted};">falta ${n(l.debe)} · ver partes</span>`}</td>
      </tr>${filaKit}`;
  }).join("");

  const filasServicios = cad.servicios.map(s => `
    <tr style="border-top:1px solid ${C.border};font-size:10.5px;vertical-align:top;">
      <td style="padding:5px 8px;"><b>${escapeHTML(s.concepto || s.articulo || "—")}</b>${s.parte ? `<div style="color:${C.muted};">de ${escapeHTML(s.parte)}</div>` : ""}</td>
      <td style="padding:5px 8px;">${escapeHTML(s.tercero || "—")}</td>
      <td style="padding:5px 8px;text-align:right;">${n(s.cantidad)}</td>
      <td style="padding:5px 8px;text-align:right;">${fmtCOP(s.total)}</td>
      <td style="padding:5px 8px;">${s.via === "ocp" ? "Orden de compra" : s.via === "cxp" ? "Cuenta de cobro" : s.via === "nada" ? "No genera" : "—"}</td>
      <td style="padding:5px 8px;color:${s.pendiente ? (s.sinDocumento ? "#b91c1c" : "#b45309") : "#047857"};font-weight:${s.sinDocumento ? 700 : 400};">
        ${s.ocp ? enlace("OCP", s.ocp, s.estado) : escapeHTML(s.estado)}</td>
    </tr>`).join("");

  const th = (h, al = "left") => `<th style="padding:6px 8px;text-align:${al};font-size:10px;color:${C.muted};">${h}</th>`;
  return `
    <div data-piloto-detalle="${escapeHTML(op.id)}">
      <button data-action="piloto-volver" style="${btnStyle(null, "secondary", true)}margin-bottom:8px;">← Pedidos</button>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:8px;margin-bottom:8px;">
        <div>
          <h2 style="margin:0;font-size:17px;font-weight:800;color:${AZUL};">${escapeHTML(op.numero || "")} · ${escapeHTML((cli && cli.nombre) || "—")}</h2>
          <div style="font-size:11px;color:${C.muted};">${fmtDate(op.fecha)}${op.numero_oc_cliente ? ` · OC cliente ${escapeHTML(op.numero_oc_cliente)}` : ""}${cad.entrega ? ` · entrega ${fmtDate(cad.entrega)}` : ""} · ${fmtCOP(op.total)}</div>
        </div>
        <div style="text-align:right;">${chip(res.estado)}<div style="font-size:10.5px;margin-top:4px;">${res.queHacer.map(t => `<div>${escapeHTML(t)}</div>`).join("")}</div></div>
      </div>
      ${avisoPiloto}
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px;">
        ${paso("CT · COTIZACIÓN", d.ct ? [d.ct._noEsta ? { numero: d.ct.numero } : d.ct] : [], "CT")}${flecha}
        ${paso("PV · PEDIDO", [op], "OP")}${flecha}
        ${paso("RQ · REQUISICIÓN", d.rqs.map(r => ({ numero: r.numero })), "")}${flecha}
        ${paso("OC · ÓRDENES DE COMPRA", d.oficiales.concat(d.informativas), "OCP")}${flecha}
        ${paso("RP · RECEPCIONES", recepcionesDeOP(op, d, ctx), "OCP")}${flecha}
        ${paso("RS · REMISIONES", d.despachos, "RC")}
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin:-4px 0 12px;font-size:10.5px;color:${C.muted};">
        <div>FP (facturas de proveedor causadas): <b style="color:${C.text};">${d.causaciones.map(x => escapeHTML(x.fc.factNum || x.ocp.numero)).join(", ") || "—"}</b></div>
        <div>· FV (facturas de venta): ${d.facturas.map(f => enlace("FACT", f)).join(" ") || "—"}</div>
        ${d.cxps.length ? `<div>· Cuentas por pagar: <b style="color:${C.text};">${d.cxps.map(x => escapeHTML(x.numero || "")).join(", ")}</b></div>` : ""}
      </div>
      ${bloqueAviso(op, res)}
      ${bloqueRQ(cad, filas, ctx, res)}
      ${bloqueApartados(cad, filas, ctx, res)}
      ${bloqueRS(cad, filas, res)}
      ${bloqueFV(cad, res)}
      <div style="font-size:12px;font-weight:800;color:${C.text};margin-bottom:4px;">Renglones del pedido</div>
      <div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;margin-bottom:12px;">
        <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
          <thead><tr style="background:#f8fafc;">${th("#")}${th("Producto")}${th("Pedido", "right")}${th("Comprado (OC) · recibido/pedido")}${th("Recibido (RP)", "right")}${th("Despachado (RS)", "right")}${th("Facturado (FV)", "right")}${th("Lo que falta")}</tr></thead>
          <tbody>${filasLineas}</tbody>
        </table>
      </div>
      <div style="font-size:12px;font-weight:800;color:${C.text};margin-bottom:4px;">Servicios y costos asociados</div>
      ${cad.servicios.length ? `
        <div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;">
          <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
            <thead><tr style="background:#f8fafc;">${th("Concepto")}${th("Tercero")}${th("Cant.", "right")}${th("Valor", "right")}${th("Destino")}${th("Documento y estado")}</tr></thead>
            <tbody>${filasServicios}</tbody>
          </table>
        </div>` : `<div style="font-size:11px;color:${C.muted};">Este pedido no tiene servicios ni costos asociados.</div>`}
    </div>`;
}

// ─── Material en transición ─────────────────────────────────────────────────
export function renderPilotoTransicion() {
  if (!state.piloto) state.piloto = { filtro: "por_gestionar" };
  const { filas } = calcular();
  const soloSinPedir = !!state.piloto.soloSinPedir;
  const clientes = state.data.clientes || [];
  const nombreCliente = (id) => (clientes.find(c => c.id === id) || {}).nombre || "—";
  const tot = filas.reduce((a, f) => {
    a.enBodega.u += f.enBodega; a.enBodega.v += f.enBodega * f.costo;
    a.comprado.u += f.comprado; a.comprado.v += f.comprado * f.costo;
    a.enRQ.u += f.enRQ; a.enRQ.v += f.enRQ * f.costo;
    a.sinPedir.u += f.sinPedir; a.sinPedir.v += f.sinPedir * f.costo;
    return a;
  }, { enBodega: { u: 0, v: 0 }, comprado: { u: 0, v: 0 }, enRQ: { u: 0, v: 0 }, sinPedir: { u: 0, v: 0 } });
  const tarjeta = (titulo, t, color, pie) => `
    <div style="background:white;border:1px solid ${C.border};border-left:4px solid ${color};border-radius:6px;padding:10px 12px;">
      <div style="font-size:10px;color:${C.muted};font-weight:700;letter-spacing:.4px;">${titulo}</div>
      <div style="font-size:16px;font-weight:800;color:${color};margin-top:2px;">${n(t.u)} uds</div>
      <div style="font-size:10px;color:${C.muted};margin-top:2px;">${fmtCOP(t.v)} a costo · ${pie}</div>
    </div>`;
  const visibles = filas.filter(f => !soloSinPedir || f.sinPedir > 0);
  const cuerpo = visibles.map(f => `
    <tr data-piloto-transicion="${escapeHTML(f.cad.op.id)}|${escapeHTML(f.c.articulo)}" style="border-top:1px solid ${C.border};vertical-align:top;font-size:11px;">
      <td style="padding:6px 8px;white-space:nowrap;">${enlace("OP", f.cad.op)}<div style="font-size:9.5px;color:${C.muted};">${fmtDate(f.cad.op.fecha)}</div></td>
      <td style="padding:6px 8px;">${escapeHTML(nombreCliente(f.cad.op.cliente_id))}</td>
      <td style="padding:6px 8px;"><b>${escapeHTML(f.c.articulo)}</b>${f.c.esParte ? `<div style="font-size:9.5px;color:#7c3aed;">parte de ${escapeHTML(f.linea.articulo)}</div>` : ""}</td>
      <td style="padding:6px 8px;text-align:right;font-weight:700;">${n(f.c.necesita)}</td>
      <td style="padding:6px 8px;text-align:right;color:#047857;">${f.enBodega ? n(f.enBodega) : "—"}</td>
      <td style="padding:6px 8px;text-align:right;color:#1d4ed8;">${f.comprado ? n(f.comprado) : "—"}${f.comprado ? `<div style="font-size:9.5px;">${f.c.ocps.filter(o => o.cantidad > o.recibido).map(o => enlace("OCP", o)).join(" ")}</div>` : ""}</td>
      <td style="padding:6px 8px;text-align:right;color:#7c3aed;">${f.enRQ ? n(f.enRQ) : "—"}</td>
      <td style="padding:6px 8px;text-align:right;color:#b91c1c;font-weight:${f.sinPedir ? 800 : 400};">${f.sinPedir ? n(f.sinPedir) : "—"}</td>
    </tr>`).join("");
  const th = (h, al = "left") => `<th style="padding:6px 8px;text-align:${al};font-size:10px;color:${C.muted};">${h}</th>`;
  return `
    <div>
      <h2 style="margin:0;font-size:17px;font-weight:800;color:${AZUL};">🚚 Material en transición</h2>
      <div style="font-size:11.5px;color:${C.muted};margin:2px 0 10px;">Lo vendido que todavía no se ha remisionado, de todas las órdenes vigentes. Cada unidad está en una sola de las cuatro partes, y suman exacto.</div>
      ${avisoPiloto}
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:8px;margin-bottom:10px;">
        ${tarjeta("📦 YA EN BODEGA", tot.enBodega, "#047857", "listo para remisionar")}
        ${tarjeta("🚚 COMPRADO, EN CAMINO", tot.comprado, "#1d4ed8", "OC sin recibir")}
        ${tarjeta("📝 PEDIDO A COMPRAS", tot.enRQ, "#7c3aed", "en requisición, sin ordenar")}
        ${tarjeta("🚨 SIN PEDIR", tot.sinPedir, "#b91c1c", "nadie lo ha pedido")}
      </div>
      <div style="font-size:10.5px;color:${C.muted};margin-bottom:8px;">La bodega es una sola: a cada orden se le asigna primero lo que llegó por sus propias compras y el resto, a la orden más antigua. Un kit se cuenta por sus partes, que son las que se compran.</div>
      <label style="display:inline-flex;gap:6px;align-items:center;font-size:11.5px;margin-bottom:8px;cursor:pointer;">
        <input type="checkbox" data-action="piloto-sin-pedir" ${soloSinPedir ? "checked" : ""}/> Ver solo lo que está sin pedir
      </label>
      ${visibles.length === 0
        ? `<div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};font-size:12.5px;">${soloSinPedir ? "🎉 No hay nada sin pedir." : "No hay mercancía pendiente de entregar."}</div>`
        : `<div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;">
            <table style="width:100%;border-collapse:collapse;">
              <thead><tr style="background:#f8fafc;">${th("Pedido")}${th("Cliente")}${th("Artículo")}${th("Falta", "right")}${th("En bodega", "right")}${th("Comprado", "right")}${th("Pedido a compras", "right")}${th("Sin pedir", "right")}</tr></thead>
              <tbody>${cuerpo}</tbody>
            </table>
          </div>`}
    </div>`;
}

// ─── Fase 2: la requisición del pedido ──────────────────────────────────────
const ESTADO_RQ = {
  pendiente: ["Por ordenar", "#7c3aed"], parcial: ["Ordenada en parte", "#b45309"],
  ordenada: ["Ordenada", "#047857"], cerrada: ["No se necesita", C.muted], sin_costo: ["El costo ya no existe", C.muted]
};
const describirLinea = (l) => l.tipo === "servicio"
  ? `🛠️ ${escapeHTML(l.concepto || "Servicio")}${l.proveedor_nombre ? ` <span style="color:${C.muted};">· ${escapeHTML(l.proveedor_nombre)}</span>` : ""}`
  : `📦 ${escapeHTML(l.articulo || "")}${l.articulo_origen ? ` <span style="color:#7c3aed;">· parte de ${escapeHTML(l.articulo_origen)}</span>` : ""}`;

function bloqueRQ(cad, filas, ctx, res) {
  const op = cad.op;
  if (op.estado === "borrador" || op.estado === "cancelada") return "";
  const rqs = rqsDeOP(op.id, ctx);
  // Solo se le pide a compras para un pedido en gestión: uno ya entregado y
  // facturado no necesita requisición (si le quedó un servicio sin documento,
  // eso es de la liquidación de la OP, no de compras).
  const enGestion = POR_GESTIONAR.includes(res.estado);
  if (!enGestion && !rqs.length) return "";
  const faltan = enGestion ? faltantesParaRQ(cad, filas, ctx) : [];
  const tablaFaltan = (titulo) => `
    <div style="font-size:11px;color:${C.text};margin:6px 0 4px;">${titulo}</div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;">
      ${faltan.map(f => `<tr style="border-top:1px solid #ede9fe;"><td style="padding:4px 6px;">${describirLinea(f)}</td><td style="padding:4px 6px;text-align:right;font-weight:700;">${n(f.cantidad)}</td></tr>`).join("")}
    </table>`;
  let cuerpo;
  if (!rqs.length) {
    cuerpo = faltan.length
      ? `${tablaFaltan(`Este pedido tiene que pedirle a compras ${faltan.length} ${faltan.length === 1 ? "renglón" : "renglones"} (solo lo que falta: lo que no está en bodega ni comprado, y los servicios sin documento):`)}
         <button data-action="piloto-crear-rq" data-id="${escapeHTML(op.id)}" style="${btnStyle("#7c3aed")}">📝 Crear la requisición</button>`
      : `<div style="font-size:11px;color:#047857;">✓ No hace falta pedirle nada a compras: lo pendiente está en bodega o ya está comprado, y los servicios tienen su documento.</div>`;
  } else {
    const filasRQ = rqs.flatMap(rq => (rq.items || []).map(l => {
      const s = saldoLinea(l, op, ctx);
      const [txt, color] = ESTADO_RQ[s.estado] || ESTADO_RQ.pendiente;
      return `
        <tr data-piloto-rq-linea="${escapeHTML(l.lid)}" style="border-top:1px solid #ede9fe;vertical-align:top;">
          <td style="padding:4px 6px;white-space:nowrap;color:${C.muted};">${escapeHTML(rq.numero || "")}</td>
          <td style="padding:4px 6px;">${describirLinea(l)}${l.cerrada && l.motivo_cierre ? `<div style="font-size:9.5px;color:${C.muted};">${escapeHTML(l.motivo_cierre)}</div>` : ""}</td>
          <td style="padding:4px 6px;text-align:right;">${n(s.cantidad)}</td>
          <td style="padding:4px 6px;text-align:right;">${n(s.ordenado)}</td>
          <td style="padding:4px 6px;">${s.docs.map(x => x.tipo === "OCP" ? enlace("OCP", x) : escapeHTML(x.numero || "")).join(" ") || "—"}</td>
          <td style="padding:4px 6px;color:${color};font-weight:700;white-space:nowrap;">${txt}</td>
          <td style="padding:4px 6px;text-align:right;">${s.pendiente > 0 ? `<button data-action="piloto-rq-cerrar" data-rq="${escapeHTML(rq.id)}" data-lid="${escapeHTML(l.lid)}" title="Ya no se necesita: sale de lo que falta por ordenar" style="${btnStyle(null, "secondary", true)}">✕</button>` : ""}</td>
        </tr>`;
    })).join("");
    cuerpo = `
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr>${["RQ", "Renglón", "Pedido", "Ordenado", "Documento", "Estado", ""].map(h => `<th style="padding:4px 6px;text-align:left;font-size:9.5px;color:${C.muted};">${h}</th>`).join("")}</tr></thead>
        <tbody>${filasRQ}</tbody>
      </table>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;">
        ${faltan.length ? `<button data-action="piloto-rq-al-dia" data-id="${escapeHTML(op.id)}" style="${btnStyle("#7c3aed", null, true)}">📝 Poner la RQ al día (+${faltan.length})</button>` : ""}
        <button data-nav="piloto-por-ordenar" style="${btnStyle(null, "secondary", true)}">Ir a lo que falta por ordenar →</button>
      </div>
      ${faltan.length ? tablaFaltan("Desde que se hizo la requisición apareció algo más por pedir:") : ""}`;
  }
  return `
    <div data-piloto-rq style="background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div style="font-size:12px;font-weight:800;color:#6d28d9;">📝 Requisición a compras (RQ)</div>
      ${cuerpo}
    </div>`;
}

// ─── Fase 2: lo que falta por ordenar ───────────────────────────────────────
// Todos los renglones de RQ con saldo, de todos los pedidos. Compras elige el
// proveedor (o el tercero y el destino de un servicio), la cantidad y el costo,
// marca los que va a ordenar y arma las órdenes: una OCP por pedido y
// proveedor, por la misma puerta de siempre; los servicios, poniéndole el
// tercero y el destino al costo de la OP y dejando que las sincronizaciones de
// siempre creen su OCP o su cuenta por pagar.
const claveLinea = (x) => `${x.rq.id}__${x.linea.lid}`;

export function renderPilotoPorOrdenar() {
  if (!state.pilotoOrdenar) state.pilotoOrdenar = { sel: {}, prov: {}, tercero: {}, via: {}, cant: {}, costo: {} };
  const po = state.pilotoOrdenar;
  const { filas, ctx } = calcular();
  const lineas = lineasPorOrdenar(state.data.pedidos || [], filas, ctx);
  const clientes = state.data.clientes || [];
  const nombreCliente = (id) => (clientes.find(c => c.id === id) || {}).nombre || "—";
  const proveedores = [...(state.data.proveedores || [])].sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")));
  const inp = `border:1px solid ${C.border};border-radius:5px;padding:4px 6px;font-size:11px;font-family:inherit;`;
  const set = (campo, k) => `onchange="state.pilotoOrdenar.${campo}['${k}']=this.value"`;
  const seleccionadas = lineas.filter(x => po.sel[claveLinea(x)]);

  const cuerpo = lineas.map(x => {
    const k = claveLinea(x), l = x.linea;
    const cant = po.cant[k] ?? x.pendiente;
    const costo = po.costo[k] ?? (parseNumber(l.costo) || getCostoPromedio(l.articulo || ""));
    const proveedor = l.tipo === "servicio"
      ? `<input list="piloto-terceros" value="${escapeHTML(po.tercero[k] ?? (l.proveedor_nombre || ""))}" placeholder="Tercero" ${set("tercero", k)} style="${inp}width:150px;"/>
         <select ${set("via", k)} style="${inp}margin-top:3px;">
           ${[["", "— destino —"], ["ocp", "🛒 Orden de compra"], ["cxp", "🧾 Cuenta de cobro"]].map(([v, t]) => `<option value="${v}" ${(po.via[k] ?? (l.via || "")) === v ? "selected" : ""}>${t}</option>`).join("")}
         </select>`
      : `<select ${set("prov", k)} style="${inp}width:170px;">
           <option value="">— proveedor —</option>
           ${proveedores.map(pv => `<option value="${escapeHTML(pv.id)}" ${po.prov[k] === pv.id ? "selected" : ""}>${escapeHTML(pv.nombre || "")}</option>`).join("")}
         </select>`;
    return `
      <tr data-piloto-por-ordenar="${escapeHTML(k)}" style="border-top:1px solid ${C.border};vertical-align:top;font-size:11px;">
        <td style="padding:6px 8px;"><input type="checkbox" data-action="piloto-ordenar-sel" data-k="${escapeHTML(k)}" ${po.sel[k] ? "checked" : ""}/></td>
        <td style="padding:6px 8px;white-space:nowrap;"><b>${escapeHTML(x.rq.numero || "")}</b><div>${enlace("OP", x.op)}</div></td>
        <td style="padding:6px 8px;">${escapeHTML(nombreCliente(x.op.cliente_id))}</td>
        <td style="padding:6px 8px;min-width:170px;">${describirLinea(l)}</td>
        <td style="padding:6px 8px;text-align:right;font-weight:700;">${n(x.pendiente)}${x.porFuera > 0 ? `<div style="font-size:9.5px;color:${C.muted};font-weight:400;">${n(x.porFuera)} ya se resolvieron por fuera</div>` : ""}</td>
        <td style="padding:6px 8px;">${proveedor}</td>
        <td style="padding:6px 8px;"><input type="number" min="0" value="${escapeHTML(String(cant))}" ${l.tipo === "servicio" ? "disabled" : set("cant", k)} style="${inp}width:70px;text-align:right;"/></td>
        <td style="padding:6px 8px;"><input type="number" min="0" value="${escapeHTML(String(Math.round(costo)))}" ${l.tipo === "servicio" ? "disabled" : set("costo", k)} style="${inp}width:90px;text-align:right;"/></td>
        <td style="padding:6px 8px;text-align:right;"><button data-action="piloto-rq-cerrar" data-rq="${escapeHTML(x.rq.id)}" data-lid="${escapeHTML(l.lid)}" title="Ya no se necesita" style="${btnStyle(null, "secondary", true)}">✕</button></td>
      </tr>`;
  }).join("");
  const th = (h, al = "left") => `<th style="padding:6px 8px;text-align:${al};font-size:10px;color:${C.muted};">${h}</th>`;
  return `
    <div>
      <h2 style="margin:0;font-size:17px;font-weight:800;color:${AZUL};">🧺 Lo que falta por ordenar</h2>
      <div style="font-size:11.5px;color:${C.muted};margin:2px 0 10px;">Lo que las requisiciones le pidieron a compras y todavía no tiene orden. Marca lo que vas a ordenar, elige el proveedor y arma las órdenes: una OCP por pedido y proveedor. Los servicios generan su orden de compra o su cuenta por pagar como siempre.</div>
      ${avisoPiloto}
      <datalist id="piloto-terceros">${proveedores.map(pv => `<option value="${escapeHTML(pv.nombre || "")}"></option>`).join("")}</datalist>
      ${lineas.length === 0
        ? `<div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};font-size:12.5px;">🎉 No hay nada pendiente por ordenar. Las requisiciones se crean desde el detalle de cada pedido.</div>`
        : `<div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;">
            <table style="width:100%;border-collapse:collapse;">
              <thead><tr style="background:#f8fafc;">${th("")}${th("RQ · Pedido")}${th("Cliente")}${th("Renglón")}${th("Falta", "right")}${th("Proveedor / tercero")}${th("Cantidad")}${th("Costo unit.")}${th("")}</tr></thead>
              <tbody>${cuerpo}</tbody>
            </table>
          </div>
          <div style="display:flex;justify-content:flex-end;margin-top:10px;">
            <button data-action="piloto-armar-ordenes" ${seleccionadas.length ? "" : "disabled"} style="${btnStyle(seleccionadas.length ? "#7c3aed" : null, seleccionadas.length ? null : "secondary")}${seleccionadas.length ? "" : "opacity:.5;cursor:not-allowed;"}">🛒 Armar órdenes (${seleccionadas.length})</button>
          </div>`}
    </div>`;
}

// Lo seleccionado, con lo que se tecleó en cada fila.
function seleccionOrdenar() {
  const po = state.pilotoOrdenar || { sel: {}, prov: {}, tercero: {}, via: {}, cant: {}, costo: {} };
  const { filas, ctx } = calcular();
  const lineas = lineasPorOrdenar(state.data.pedidos || [], filas, ctx);
  return lineas.filter(x => po.sel[claveLinea(x)]).map(x => {
    const k = claveLinea(x), l = x.linea;
    return {
      ...x,
      proveedor: (state.data.proveedores || []).find(pv => pv.id === po.prov[k]) || null,
      proveedor_nombre: (po.tercero[k] ?? (l.proveedor_nombre || "")).trim(),
      via: po.via[k] ?? (l.via || ""),
      cantidad: l.tipo === "servicio" ? x.pendiente : parseNumber(po.cant[k] ?? x.pendiente),
      costo: parseNumber(po.costo[k] ?? (parseNumber(l.costo) || getCostoPromedio(l.articulo || "")))
    };
  });
}

function mostrarVistaPreviaOrdenes() {
  const { ocps, servicios, errores } = agruparOrdenes(seleccionOrdenar());
  if (errores.length) { alert("⚠️ Antes de armar las órdenes:\n\n• " + errores.join("\n• ")); return; }
  const tarjetaOcp = (g) => {
    const sub = g.items.reduce((s, it) => s + it.total, 0);
    return `
      <div style="border:1px solid ${C.border};border-radius:8px;padding:8px 10px;margin-bottom:8px;">
        <div style="font-size:11.5px;font-weight:800;color:${C.text};">🛒 OCP para ${escapeHTML(g.proveedor.nombre || "")} · ${escapeHTML(g.op.numero || "")}</div>
        ${g.items.map(it => `<div style="font-size:11px;display:flex;justify-content:space-between;"><span>${escapeHTML(it.articulo)} × ${n(it.cantidad)}</span><span>${fmtCOP(it.total)}</span></div>`).join("")}
        <div style="font-size:11px;text-align:right;font-weight:700;margin-top:3px;">Subtotal ${fmtCOP(sub)} + IVA según el régimen de cada artículo</div>
      </div>`;
  };
  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1180;padding:11px;">
      <div data-action="prevent-close" data-piloto-vista-previa style="background:white;border-radius:14px;width:560px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};background:#f8fafc;font-size:15px;font-weight:800;color:${C.text};">🛒 Órdenes que se van a crear</div>
        <div style="padding:12px 14px;">
          ${ocps.map(tarjetaOcp).join("")}
          ${servicios.length ? `
            <div style="border:1px solid ${C.border};border-radius:8px;padding:8px 10px;margin-bottom:8px;">
              <div style="font-size:11.5px;font-weight:800;color:${C.text};margin-bottom:3px;">🛠️ Servicios (se generan con su destino, como al guardar los costos de la OP)</div>
              ${servicios.map(s => `<div style="font-size:11px;">${escapeHTML(s.linea.concepto || "")} · ${escapeHTML(s.op.numero || "")} → ${escapeHTML(s.proveedor_nombre)} · ${s.via === "ocp" ? "orden de compra" : "cuenta de cobro"}</div>`).join("")}
            </div>` : ""}
          <div style="font-size:10.5px;color:${C.muted};margin-bottom:10px;">Las OCP quedan en borrador en Órdenes de Compra, amarradas a su pedido y al renglón de la requisición.</div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="close-modal" style="${btnStyle(null, "secondary")}">Cancelar</button>
            <button data-action="piloto-crear-ordenes" style="${btnStyle("#7c3aed")}">✅ Crear</button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

async function crearOrdenes() {
  if (state._pilotoCreando) return;
  state._pilotoCreando = true;
  try {
    const { ocps, servicios, errores } = agruparOrdenes(seleccionOrdenar());
    if (errores.length) { alert("⚠️ " + errores.join("\n")); return; }
    const nuevas = [];
    for (const g of ocps) {
      const subtotal = g.items.reduce((s, it) => s + it.total, 0);
      const iva = desgloseIVA(g.items).iva;
      const ocp = {
        id: genId(), numero: await siguienteNumeroDoc("OCP"), fecha: today(), proveedor_id: g.proveedor.id,
        items: g.items, subtotal, iva, total: subtotal + iva, estado: "borrador", estado_pago: "por_pagar",
        notas: `Desde la requisición (flujo piloto).`,
        _origen: { tipo: "OP", id: g.op.id, numero: g.op.numero, fecha: g.op.fecha }
      };
      sellarAutor(ocp);
      nuevas.push(ocp);
    }
    if (nuevas.length) {
      saveData("ocp", [...(state.data.ocp || []), ...nuevas]);
      const porOp = new Map();
      nuevas.forEach(o => porOp.set(o._origen.id, [...(porOp.get(o._origen.id) || []), o]));
      saveData("pedidos", (state.data.pedidos || []).map(p => porOp.has(p.id)
        ? { ...p, _convertedTo: [...getConvertedToList(p), ...porOp.get(p.id).map(o => ({ tipo: "OCP", id: o.id, numero: o.numero, fecha: new Date().toISOString() }))] }
        : p));
    }
    // Servicios: el tercero y el destino van al costo, y las sincronizaciones
    // de siempre crean la orden de compra o la cuenta por pagar.
    const opsServ = [...new Set(servicios.map(s => s.op.id))];
    let generados = 0;
    for (const opId of opsServ) {
      const op = (state.data.pedidos || []).find(p => p.id === opId);
      if (!op) continue;
      const copia = { ...op, _conceptosCosto: (op._conceptosCosto || []).map(c => ({ ...c })) };
      servicios.filter(s => s.op.id === opId).forEach(s => {
        const c = copia._conceptosCosto.find(x => x._ccid === s.linea.ccid);
        if (!c) return;
        c.proveedor_nombre = s.proveedor_nombre;
        c._via = s.via;
        generados++;
      });
      await sincronizarOcpDeCostos(copia);
      await sincronizarCxpDirectasOP(copia);
      saveData("pedidos", (state.data.pedidos || []).map(p => p.id === opId ? copia : p));
    }
    state.pilotoOrdenar = { sel: {}, prov: {}, tercero: {}, via: {}, cant: {}, costo: {} };
    state.modal = null;
    render();
    const partes = [];
    if (nuevas.length) partes.push(`${nuevas.length === 1 ? "Se creó la orden de compra" : "Se crearon las órdenes de compra"} ${nuevas.map(o => o.numero).join(", ")} (en borrador)`);
    if (generados) partes.push(`${generados} servicio${generados === 1 ? "" : "s"} con su orden de compra o cuenta por pagar`);
    alert("✅ " + partes.join(".\n") + ".");
  } finally {
    state._pilotoCreando = false;
  }
}

async function crearOPonerAlDiaRQ(opId) {
  const { pedidos, filas, ctx } = calcular();
  const p = pedidos.find(x => x.cad.op.id === opId);
  if (!p) return;
  const faltan = faltantesParaRQ(p.cad, filas, ctx);
  if (!faltan.length) { alert("No hay nada nuevo que pedir a compras."); return; }
  const nuevos = faltan.map(f => ({ lid: genId(), ...f, cerrada: false }));
  const existente = rqsDeOP(opId, ctx)[0];
  if (existente) {
    saveData("requisiciones", (state.data.requisiciones || []).map(r => r.id === existente.id ? { ...r, items: [...(r.items || []), ...nuevos] } : r));
    render();
    alert(`✅ ${existente.numero}: se agregaron ${nuevos.length} ${nuevos.length === 1 ? "renglón" : "renglones"}.`);
    return;
  }
  const op = p.cad.op;
  const rq = { id: genId(), numero: await siguienteNumeroDoc("RQ"), fecha: today(), pedido_id: op.id, pedido_numero: op.numero,
    cliente_id: op.cliente_id, estado: "abierta", items: nuevos };
  sellarAutor(rq);
  saveData("requisiciones", [...(state.data.requisiciones || []), rq]);
  render();
  alert(`✅ Se creó la requisición ${rq.numero} con ${nuevos.length} ${nuevos.length === 1 ? "renglón" : "renglones"}. Compras la ve en «Lo que falta por ordenar».`);
}

// ─── Fase 3: recepciones (RP), aviso y apartado ─────────────────────────────
// La RP de la cadena: las del piloto con su número y, de las recepciones de
// antes (o del ingreso de siempre), la OCP con cuántas lleva.
function recepcionesDeOP(op, d, ctx) {
  const rps = (ctx.recepciones || []).filter(rp => rp.pedido_id === op.id || (rp.lineas || []).some(l => (l.destinos || []).some(x => x.pedido_id === op.id)));
  const conRp = new Set(rps.map(rp => rp.ocp_id));
  const sueltas = d.oficiales.filter(o => (o._recepciones || []).some(r => !r.rp_id) && !conRp.has(o.id))
    .map(o => ({ id: o.id, numero: `${o.numero} (${(o._recepciones || []).filter(r => !r.rp_id).length})` }));
  return [...rps.map(rp => ({ id: rp.ocp_id, numero: rp.numero })), ...sueltas];
}

function bloqueAviso(op, res) {
  if (!res.avisos.length || !POR_GESTIONAR.includes(res.estado)) return "";
  return `
    <div data-piloto-aviso style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div style="font-size:12px;font-weight:800;color:#047857;">📦 Llegó material para este pedido</div>
      ${res.avisos.map(rp => `
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;font-size:11px;margin-top:4px;">
          <span><b>${escapeHTML(rp.numero)}</b> (${fmtDate(rp.fecha)}) · ${loQueLlego(rp, op.id).map(x => `${escapeHTML(x.articulo)} × ${n(x.cantidad)}`).join(", ")} — quedó apartado: listo para remisionar.</span>
          <button data-action="piloto-aviso-visto" data-rp="${escapeHTML(rp.id)}" data-op="${escapeHTML(op.id)}" style="${btnStyle("#047857", null, true)}">Visto</button>
        </div>`).join("")}
    </div>`;
}

function bloqueApartados(cad, filas, ctx, res) {
  const op = cad.op;
  if (op.estado === "borrador" || op.estado === "cancelada") return "";
  const mias = filas.filter(f => f.cad.op.id === op.id);
  // Lo apartado de un artículo que ya se remisionó entero se consumió: no se
  // lista (el apartado vigente nunca pasa de lo que falta entregar).
  const vigentePorArt = new Map();
  mias.forEach(f => vigentePorArt.set(normalize(f.c.articulo || ""), (vigentePorArt.get(normalize(f.c.articulo || "")) || 0) + f.apartado));
  const vigentes = apartadosDeOP(op.id, ctx).filter(a => (vigentePorArt.get(normalize(a.articulo || "")) || 0) > 0);
  const sinApartar = mias.filter(f => f.enBodega - f.apartado + f.apartadoSinStock > 0);
  if (!vigentes.length && !(sinApartar.length && POR_GESTIONAR.includes(res.estado))) return "";
  return `
    <div data-piloto-apartados style="background:#f0fdfa;border:1px solid #99f6e4;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div style="font-size:12px;font-weight:800;color:#0f766e;">📌 Apartado de bodega</div>
      <div style="font-size:10.5px;color:${C.muted};margin:2px 0 6px;">Mercancía reservada para este pedido, sin vencimiento: no se le asigna a otro pedido en el piloto. Se consume sola al remisionar; si ya no se necesita, se libera.</div>
      ${vigentes.length ? `<table style="width:100%;border-collapse:collapse;font-size:11px;">
        ${vigentes.map(a => `
          <tr data-piloto-apartado="${escapeHTML(a.id)}" style="border-top:1px solid #ccfbf1;">
            <td style="padding:4px 6px;">${escapeHTML(a.articulo)}</td>
            <td style="padding:4px 6px;text-align:right;font-weight:700;">${n(parseNumber(a.cantidad))}</td>
            <td style="padding:4px 6px;color:${C.muted};">${a.origen && a.origen.tipo === "RP" ? `de ${escapeHTML(a.origen.numero || "")}` : "a mano"} · ${fmtDate(a.fecha)}</td>
            <td style="padding:4px 6px;text-align:right;"><button data-action="piloto-liberar" data-id="${escapeHTML(a.id)}" style="${btnStyle(null, "secondary", true)}">Liberar</button></td>
          </tr>`).join("")}
      </table>` : ""}
      ${sinApartar.length && POR_GESTIONAR.includes(res.estado) ? `
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:6px;font-size:11px;">
          <span>En bodega sin apartar para este pedido: ${sinApartar.map(f => `${escapeHTML(f.c.articulo)} × ${n(f.enBodega - f.apartado + f.apartadoSinStock)}`).join(", ")}</span>
          <button data-action="piloto-apartar" data-id="${escapeHTML(op.id)}" style="${btnStyle("#0f766e", null, true)}">📌 Apartar</button>
        </div>` : ""}
    </div>`;
}

// ─── Recepciones (RP) ───────────────────────────────────────────────────────
// Solo entra a la bodega lo que tiene ficha de producto y no es un kit (un kit
// no se compra: se compran sus partes). Las líneas de servicio de una OCP
// (marcación, transporte) no se reciben aquí.
const inventariable = (art) => {
  const p = (state.data.productos || []).find(x => normalize(x.nombre || "") === normalize(art || ""));
  return !!p && !p.es_kit;
};
function pendientesDeOCP(o) {
  return (o.items || []).filter(it => inventariable(it.articulo)).map(it => {
    const recibido = ((o._recepciones || [])).reduce((s, r) => s + (r.items || []).filter(x => normalize(x.articulo || "") === normalize(it.articulo || "")).reduce((t, x) => t + (parseNumber(x.cantidad) || 0), 0), 0);
    return { articulo: it.articulo, pedido: parseNumber(it.cantidad) || 0, recibido, falta: Math.max(0, (parseNumber(it.cantidad) || 0) - recibido), costo: parseNumber(it.precio_unitario ?? it.costo) || 0 };
  }).filter(x => x.falta > 0);
}

export function renderPilotoRecepciones() {
  const ocps = (state.data.ocp || []).filter(o => o.estado !== "cancelada" && !esOcpInformativa(o) && pendientesDeOCP(o).length);
  const prov = (id) => ((state.data.proveedores || []).find(p => p.id === id) || {}).nombre || "—";
  const rp = state.pilotoRP;
  const th = (h, al = "left") => `<th style="padding:6px 8px;text-align:${al};font-size:10px;color:${C.muted};">${h}</th>`;
  const inp = `border:1px solid ${C.border};border-radius:5px;padding:4px 6px;font-size:11px;font-family:inherit;width:80px;text-align:right;`;

  const listaOcp = ocps.map(o => `
    <tr data-piloto-ocp="${escapeHTML(o.id)}" style="border-top:1px solid ${C.border};font-size:11px;vertical-align:top;">
      <td style="padding:6px 8px;white-space:nowrap;">${enlace("OCP", o)}<div style="font-size:9.5px;color:${C.muted};">${fmtDate(o.fecha)} · ${escapeHTML(o.estado || "")}</div></td>
      <td style="padding:6px 8px;">${escapeHTML(prov(o.proveedor_id))}</td>
      <td style="padding:6px 8px;">${o._origen && o._origen.tipo === "OP" ? enlace("OP", (state.data.pedidos || []).find(p => p.id === o._origen.id), o._origen.numero) : "—"}</td>
      <td style="padding:6px 8px;">${pendientesDeOCP(o).map(x => `${escapeHTML(x.articulo)}: faltan <b>${n(x.falta)}</b> de ${n(x.pedido)}`).join("<br>")}</td>
      <td style="padding:6px 8px;text-align:right;"><button data-action="piloto-rp-abrir" data-id="${escapeHTML(o.id)}" style="${btnStyle("#0f766e", null, true)}">📥 Recibir</button></td>
    </tr>`).join("");

  let formulario = "";
  const ocpSel = rp && (state.data.ocp || []).find(o => o.id === rp.ocpId);
  if (ocpSel) {
    formulario = `
      <div data-piloto-rp-form style="background:white;border:1.5px solid #5eead4;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
        <div style="font-size:12.5px;font-weight:800;color:#0f766e;margin-bottom:6px;">📥 Recibir ${escapeHTML(ocpSel.numero || "")} · ${escapeHTML(prov(ocpSel.proveedor_id))}</div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead><tr>${th("Artículo")}${th("Faltan", "right")}${th("Llega ahora", "right")}${th("Costo unit.", "right")}</tr></thead>
          <tbody>${pendientesDeOCP(ocpSel).map(x => `
            <tr style="border-top:1px solid ${C.border};">
              <td style="padding:5px 8px;">${escapeHTML(x.articulo)}</td>
              <td style="padding:5px 8px;text-align:right;">${n(x.falta)}</td>
              <td style="padding:5px 8px;text-align:right;"><input type="number" min="0" data-piloto-rp-cant="${escapeHTML(x.articulo)}" value="${escapeHTML(String(rp.cant[x.articulo] ?? x.falta))}" onchange="state.pilotoRP.cant[this.dataset.pilotoRpCant]=this.value" style="${inp}"/></td>
              <td style="padding:5px 8px;text-align:right;"><input type="number" min="0" data-piloto-rp-costo="${escapeHTML(x.articulo)}" value="${escapeHTML(String(rp.costo[x.articulo] ?? x.costo))}" onchange="state.pilotoRP.costo[this.dataset.pilotoRpCosto]=this.value" style="${inp}"/></td>
            </tr>`).join("")}</tbody>
        </table>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px;">
          <button data-action="piloto-rp-cancelar" style="${btnStyle(null, "secondary", true)}">Cancelar</button>
          <button data-action="piloto-rp-reparto" style="${btnStyle("#0f766e", null, true)}">Ver reparto y registrar →</button>
        </div>
      </div>`;
  }

  const rps = [...(state.data.recepciones || [])].sort((a, b) => String(b.numero || "").localeCompare(String(a.numero || "")));
  const listaRp = rps.map(r => {
    const o = (state.data.ocp || []).find(x => x.id === r.ocp_id);
    const cruce = o ? cruceOCP(o, inventariable) : [];
    const porCausar = cruce.filter(c => c.porCausar > 0).map(c => `${escapeHTML(c.articulo)} ${n(c.porCausar)}`);
    const deMas = cruce.filter(c => c.deMas > 0).map(c => `${escapeHTML(c.articulo)} ${n(c.deMas)}`);
    return `
      <tr data-piloto-rp="${escapeHTML(r.id)}" style="border-top:1px solid ${C.border};font-size:11px;vertical-align:top;">
        <td style="padding:6px 8px;white-space:nowrap;"><b>${escapeHTML(r.numero || "")}</b><div style="font-size:9.5px;color:${C.muted};">${fmtDate(r.fecha)}</div></td>
        <td style="padding:6px 8px;">${o ? enlace("OCP", o) : escapeHTML(r.ocp_numero || "—")}</td>
        <td style="padding:6px 8px;">${(r.lineas || []).map(l => `<div><b>${escapeHTML(l.articulo)} × ${n(parseNumber(l.cantidad))}</b> → ${[...(l.destinos || []).map(d => `${escapeHTML(d.pedido_numero || "")} ${n(parseNumber(d.cantidad))}`), parseNumber(l.inventario) > 0 ? `inventario ${n(parseNumber(l.inventario))}` : ""].filter(Boolean).join(" · ")}</div>`).join("")}</td>
        <td style="padding:6px 8px;">${deMas.length ? `<div style="color:#b91c1c;font-weight:700;">⚠️ Causado de más: ${deMas.join(", ")}</div>` : ""}${porCausar.length ? `<div style="color:#b45309;">Por causar: ${porCausar.join(", ")}</div>` : ""}${!deMas.length && !porCausar.length ? `<span style="color:#047857;">✓ al día</span>` : ""}</td>
      </tr>`;
  }).join("");

  return `
    <div>
      <h2 style="margin:0;font-size:17px;font-weight:800;color:${AZUL};">📥 Recepciones (RP)</h2>
      <div style="font-size:11.5px;color:${C.muted};margin:2px 0 10px;">Lo que llega del proveedor entra a la bodega por el mismo ingreso de siempre, y además se reparte: primero al pedido que lo compró, luego a los que lo esperan (del más antiguo al más nuevo), y el resto queda libre. A cada pedido se le aparta lo suyo y se le avisa.</div>
      ${avisoPiloto}
      ${formulario}
      <div style="font-size:12px;font-weight:800;color:${C.text};margin-bottom:4px;">Órdenes de compra por recibir</div>
      ${ocps.length ? `<div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;margin-bottom:14px;">
          <table style="width:100%;border-collapse:collapse;"><thead><tr style="background:#f8fafc;">${th("OCP")}${th("Proveedor")}${th("Pedido")}${th("Lo que falta")}${th("")}</tr></thead><tbody>${listaOcp}</tbody></table>
        </div>` : `<div style="padding:16px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};font-size:12px;margin-bottom:14px;">No hay órdenes de compra con mercancía por recibir.</div>`}
      <div style="font-size:12px;font-weight:800;color:${C.text};margin-bottom:4px;">Recepciones hechas · cruce con la factura del proveedor (FP)</div>
      ${rps.length ? `<div style="overflow-x:auto;background:white;border:1px solid ${C.border};border-radius:8px;">
          <table style="width:100%;border-collapse:collapse;"><thead><tr style="background:#f8fafc;">${th("RP")}${th("OCP")}${th("Lo que llegó y a quién")}${th("Factura del proveedor")}</tr></thead><tbody>${listaRp}</tbody></table>
        </div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:6px;">La factura del proveedor se causa en <span data-nav="fact-prov" style="color:#0F766E;font-weight:700;cursor:pointer;text-decoration:underline;">Facturas Proveedor</span>. Lo que se causa debe ser lo recibido: si se causa de más, aquí sale la alarma.</div>`
        : `<div style="font-size:11px;color:${C.muted};">Todavía no hay recepciones registradas desde el piloto.</div>`}
    </div>`;
}

function itemsRecepcion() {
  const rp = state.pilotoRP;
  const o = rp && (state.data.ocp || []).find(x => x.id === rp.ocpId);
  if (!o) return { o: null, items: [] };
  const items = pendientesDeOCP(o).map(x => ({
    articulo: x.articulo,
    cantidad: Math.min(x.falta, Math.max(0, parseNumber(rp.cant[x.articulo] ?? x.falta) || 0)),
    precio: parseNumber(rp.costo[x.articulo] ?? x.costo) || 0
  })).filter(x => x.cantidad > 0);
  return { o, items };
}

function mostrarRepartoRecepcion() {
  const { o, items } = itemsRecepcion();
  if (!o) return;
  if (!items.length) { alert("Escribe cuánto llega de al menos un artículo."); return; }
  const { filas } = calcular();
  const reparto = repartirRecepcion(o, items, filas);
  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1180;padding:11px;">
      <div data-action="prevent-close" data-piloto-reparto style="background:white;border-radius:14px;width:560px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};background:#f8fafc;font-size:15px;font-weight:800;color:${C.text};">📥 Recepción de ${escapeHTML(o.numero || "")}: así se reparte</div>
        <div style="padding:12px 14px;">
          ${reparto.map(r => `
            <div style="border:1px solid ${C.border};border-radius:8px;padding:8px 10px;margin-bottom:8px;font-size:11.5px;">
              <div style="font-weight:800;">${escapeHTML(r.articulo)} × ${n(r.cantidad)} <span style="font-weight:400;color:${C.muted};">a ${fmtCOP(r.precio)} c/u</span></div>
              ${r.destinos.map(d => `<div>📌 ${escapeHTML(d.pedido_numero || "")}: <b>${n(d.cantidad)}</b> apartados</div>`).join("")}
              ${r.inventario > 0 ? `<div style="color:${C.muted};">📦 ${n(r.inventario)} quedan libres en inventario</div>` : ""}
            </div>`).join("")}
          <div style="font-size:10.5px;color:${C.muted};margin-bottom:10px;">Entra al kardex por el ingreso de siempre (a costo de la línea), queda en el historial de la OCP y nace la RP con su consecutivo. A cada pedido se le aparta lo suyo y se le avisa.</div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="close-modal" style="${btnStyle(null, "secondary")}">Cancelar</button>
            <button data-action="piloto-rp-registrar" style="${btnStyle("#0f766e")}">✅ Registrar la recepción</button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

async function registrarRecepcion() {
  if (state._pilotoRecibiendo) return;
  state._pilotoRecibiendo = true;
  try {
    const { o, items } = itemsRecepcion();
    if (!o || !items.length) return;
    const { filas } = calcular();
    const reparto = repartirRecepcion(o, items, filas);
    const rpId = genId();
    const numero = await siguienteNumeroDoc("RP");
    const ocpAct = aplicarIngresoOCP(o.id, items, { rp_id: rpId, rp_numero: numero });
    if (!ocpAct) { alert("⚠️ No se encontró la orden de compra."); return; }
    const rp = {
      id: rpId, numero, fecha: today(), ocp_id: o.id, ocp_numero: o.numero, proveedor_id: o.proveedor_id,
      pedido_id: o._origen && o._origen.tipo === "OP" ? o._origen.id : null,
      lineas: reparto.map(r => ({ articulo: r.articulo, cantidad: r.cantidad, costo: r.precio, destinos: r.destinos, inventario: r.inventario })),
      avisos_vistos: []
    };
    sellarAutor(rp);
    saveData("recepciones", [...(state.data.recepciones || []), rp]);
    const nuevos = reparto.flatMap(r => r.destinos.map(d => {
      const a = { id: genId(), fecha: today(), pedido_id: d.pedido_id, pedido_numero: d.pedido_numero, articulo: r.articulo, cantidad: d.cantidad, origen: { tipo: "RP", id: rpId, numero } };
      sellarAutor(a);
      return a;
    }));
    if (nuevos.length) saveData("apartados", [...(state.data.apartados || []), ...nuevos]);
    state.pilotoRP = null;
    state.modal = null;
    render();
    const pedidos = [...new Set(nuevos.map(a => a.pedido_numero))];
    alert(`✅ ${numero}: entró a la bodega lo recibido de ${o.numero}.` +
      (pedidos.length ? `\n\nQuedó apartado y con aviso para: ${pedidos.join(", ")}.` : "\n\nNingún pedido lo estaba esperando: quedó libre en inventario."));
  } finally {
    state._pilotoRecibiendo = false;
  }
}

function apartarLoDeBodega(opId) {
  const { filas } = calcular();
  const op = (state.data.pedidos || []).find(p => p.id === opId);
  const nuevos = filas.filter(f => f.cad.op.id === opId).map(f => ({ f, cant: f.enBodega - f.apartado + f.apartadoSinStock })).filter(x => x.cant > 0)
    .map(({ f, cant }) => {
      const a = { id: genId(), fecha: today(), pedido_id: opId, pedido_numero: op && op.numero, articulo: f.c.articulo, cantidad: cant, origen: { tipo: "manual" } };
      sellarAutor(a);
      return a;
    });
  if (!nuevos.length) return;
  saveData("apartados", [...(state.data.apartados || []), ...nuevos]);
  render();
  alert(`📌 Apartado para ${op && op.numero}: ${nuevos.map(a => `${a.articulo} × ${n(a.cantidad)}`).join(", ")}.`);
}

// ─── Fase 4: la remisión (RS) y la factura (FV) desde el pedido ─────────────
// Lo que el flujo de siempre deja despachar de un artículo para esta OP: lo
// mismo que mira la conversión OP → RS (lo facturado por adelantado a otro
// cliente no se puede llevar). Para un kit, cuántos se pueden armar.
const disponibleFlujo = (op, art) => {
  const kit = stockDisponibleKit(op, art, op.id);
  return kit != null ? kit : getStockDisponible(art, op.id);
};

function bloqueRS(cad, filas, res) {
  const op = cad.op;
  if (!["por_remisionar", "parcial"].includes(res.estado)) return "";
  const prep = preparacionRS(cad, filas, disponibleFlujo);
  const borradores = remisionesEnBorrador(cad);
  const algo = prep.some(p => p.tope > 0);
  const inp = `border:1px solid ${C.border};border-radius:5px;padding:4px 6px;font-size:11px;font-family:inherit;width:70px;text-align:right;`;
  const cant = (state.pilotoRS && state.pilotoRS.opId === op.id) ? state.pilotoRS.cant : {};
  return `
    <div data-piloto-rs style="background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div style="font-size:12px;font-weight:800;color:#c2410c;">🚚 Remisión (RS)</div>
      <div style="font-size:10.5px;color:${C.muted};margin:2px 0 6px;">Lo que se puede despachar hoy: lo apartado para este pedido más lo libre que le toca en bodega. Nunca más.</div>
      ${borradores.length ? `<div style="font-size:11px;color:#b45309;margin-bottom:6px;">📝 En borrador (todavía no salió de bodega): ${borradores.map(r => enlace("RC", r)).join(" ")} — complétala para que despache.</div>` : ""}
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr>${["Renglón", "Falta", "Se puede", "Despachar", "Por qué no alcanza"].map((h, i) => `<th style="padding:4px 6px;text-align:${i && i < 4 ? "right" : "left"};font-size:9.5px;color:${C.muted};">${h}</th>`).join("")}</tr></thead>
        <tbody>${prep.map(p => `
          <tr data-piloto-rs-linea="${p.idx}" style="border-top:1px solid #ffedd5;vertical-align:top;">
            <td style="padding:4px 6px;">${escapeHTML(p.articulo)}${p.esKit ? ` <span style="color:#7c3aed;font-size:9.5px;">(kit)</span>` : ""}</td>
            <td style="padding:4px 6px;text-align:right;">${n(p.debe)}</td>
            <td style="padding:4px 6px;text-align:right;font-weight:700;color:${p.tope > 0 ? "#047857" : "#b91c1c"};">${n(p.tope)}</td>
            <td style="padding:4px 6px;text-align:right;"><input type="number" min="0" data-piloto-rs-cant="${p.idx}" ${p.tope > 0 ? "" : "disabled"} value="${escapeHTML(String(cant[p.idx] ?? p.tope))}" onchange="state.pilotoRS={opId:'${escapeHTML(op.id)}',cant:Object.assign((state.pilotoRS&&state.pilotoRS.opId==='${escapeHTML(op.id)}'?state.pilotoRS.cant:{}),{[this.dataset.pilotoRsCant]:this.value})}" style="${inp}"/></td>
            <td style="padding:4px 6px;color:${C.muted};font-size:10.5px;">${escapeHTML(p.motivo || (p.tope >= p.debe ? "✓ todo disponible" : ""))}</td>
          </tr>`).join("")}</tbody>
      </table>
      <div style="display:flex;justify-content:flex-end;margin-top:8px;">
        <button data-action="piloto-armar-rs" data-id="${escapeHTML(op.id)}" ${algo ? "" : "disabled"} style="${btnStyle(algo ? "#c2410c" : null, algo ? null : "secondary", true)}${algo ? "" : "opacity:.5;cursor:not-allowed;"}">🚚 Armar la remisión</button>
      </div>
    </div>`;
}

function bloqueFV(cad, res) {
  if (!["parcial", "por_facturar"].includes(res.estado)) return "";
  const op = cad.op;
  const pendientes = remisionesPorFacturar(cad);
  if (!pendientes.length) return "";
  const sel = (state.pilotoFV && state.pilotoFV.opId === op.id) ? state.pilotoFV.sel : {};
  const marcadas = pendientes.filter(r => sel[r.id]);
  return `
    <div data-piloto-fv style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div style="font-size:12px;font-weight:800;color:#1d4ed8;">🧾 Factura (FV)</div>
      <div style="font-size:10.5px;color:${C.muted};margin:2px 0 6px;">Remisiones que ya salieron de bodega y ninguna factura cubre. Marca las que van juntas: sale UNA factura, por el mismo camino de siempre (Dataico, local o registrada por fuera).</div>
      ${pendientes.map(r => `
        <label data-piloto-fv-rs="${escapeHTML(r.id)}" style="display:flex;gap:8px;align-items:center;font-size:11px;padding:3px 0;cursor:pointer;">
          <input type="checkbox" data-action="piloto-fv-sel" data-op="${escapeHTML(op.id)}" data-rc="${escapeHTML(r.id)}" ${sel[r.id] ? "checked" : ""}/>
          <span>${enlace("RC", r)} · ${fmtDate(r.fecha)} · ${(r.items || []).map(it => `${escapeHTML(it.articulo || "")} × ${n(parseNumber(it.cantidad))}`).join(", ")}</span>
          <span style="margin-left:auto;font-weight:700;">${fmtCOP(valorRemision(r))}</span>
        </label>`).join("")}
      <div style="display:flex;justify-content:flex-end;margin-top:8px;">
        <button data-action="piloto-facturar" data-id="${escapeHTML(op.id)}" ${marcadas.length ? "" : "disabled"} style="${btnStyle(marcadas.length ? "#1d4ed8" : null, marcadas.length ? null : "secondary", true)}${marcadas.length ? "" : "opacity:.5;cursor:not-allowed;"}">🧾 Facturar ${marcadas.length ? `(${marcadas.length})` : ""}</button>
      </div>
    </div>`;
}

function armarRemisionDesdePedido(opId) {
  const { pedidos, filas } = calcular();
  const p = pedidos.find(x => x.cad.op.id === opId);
  if (!p) return;
  const op = p.cad.op;
  const prep = preparacionRS(p.cad, filas, disponibleFlujo);
  const cant = (state.pilotoRS && state.pilotoRS.opId === opId) ? state.pilotoRS.cant : {};
  const { items, errores } = armarRS(prep, cant);
  if (errores.length) { alert("⚠️ " + errores.join("\n")); return; }
  // El MISMO documento que arma la conversión OP → RS de siempre, con las
  // cantidades del piloto y su tope por renglón para el guardado.
  const origen = (op.items || []);
  const topes = {};
  prep.forEach(x => { topes[normalize(x.articulo || "")] = x.tope; });
  state.editingDoc = {
    fecha: today(),
    cliente_id: op.cliente_id || "",
    proveedor_id: "",
    items: items.map(it => {
      const o = origen.find(x => normalize(x.articulo || "") === normalize(it.articulo || "")) || {};
      return { articulo: it.articulo, descripcion: o.descripcion || it.descripcion || "", referencia: o.referencia || "", cantidad: it.cantidad, bolsas: 1, observaciones: "" };
    }),
    notas: `Desde el pedido ${op.numero} (flujo piloto)`,
    estado: "borrador",
    _origen: { tipo: "OP", id: op.id, numero: op.numero, fecha: op.fecha },
    ...(op.asesor_comercial ? { asesor_comercial: op.asesor_comercial } : {}),
    _topesPiloto: topes
  };
  state.editingTipo = "RC";
  state.isConversion = true;
  state.pilotoRS = null;
  renderDocFormModal();
}

registerActions({
  "piloto-filtro": (d) => { state.piloto = { ...(state.piloto || {}), filtro: d.f || "por_gestionar" }; render(); },
  "piloto-abrir": (d) => { state.piloto = { ...(state.piloto || {}), pedidoId: d.id }; state.page = "piloto-pedidos"; render(); },
  "piloto-volver": () => { state.piloto = { ...(state.piloto || {}), pedidoId: null }; render(); },
  "piloto-sin-pedir": () => { state.piloto = { ...(state.piloto || {}), soloSinPedir: !(state.piloto && state.piloto.soloSinPedir) }; render(); },
  "piloto-crear-rq": (d) => crearOPonerAlDiaRQ(d.id),
  "piloto-rq-al-dia": (d) => crearOPonerAlDiaRQ(d.id),
  "piloto-rq-cerrar": (d) => {
    const rq = (state.data.requisiciones || []).find(r => r.id === d.rq);
    const l = rq && (rq.items || []).find(x => x.lid === d.lid);
    if (!l) return;
    const motivo = (window.prompt(`¿Por qué ya no se necesita «${l.articulo || l.concepto}»? (queda anotado en la requisición)`) || "").trim();
    if (!motivo) return;
    saveData("requisiciones", state.data.requisiciones.map(r => r.id === rq.id
      ? { ...r, items: r.items.map(x => x.lid === l.lid ? { ...x, cerrada: true, motivo_cierre: motivo } : x) } : r));
    render();
  },
  "piloto-ordenar-sel": (d) => {
    if (!state.pilotoOrdenar) state.pilotoOrdenar = { sel: {}, prov: {}, tercero: {}, via: {}, cant: {}, costo: {} };
    state.pilotoOrdenar.sel[d.k] = !state.pilotoOrdenar.sel[d.k];
    render();
  },
  "piloto-armar-ordenes": () => mostrarVistaPreviaOrdenes(),
  "piloto-crear-ordenes": () => crearOrdenes(),
  "piloto-rp-abrir": (d) => { state.pilotoRP = { ocpId: d.id, cant: {}, costo: {} }; render(); },
  "piloto-rp-cancelar": () => { state.pilotoRP = null; render(); },
  "piloto-rp-reparto": () => mostrarRepartoRecepcion(),
  "piloto-rp-registrar": () => registrarRecepcion(),
  "piloto-aviso-visto": (d) => {
    saveData("recepciones", (state.data.recepciones || []).map(r => r.id === d.rp ? { ...r, avisos_vistos: [...new Set([...(r.avisos_vistos || []), d.op])] } : r));
    render();
  },
  "piloto-apartar": (d) => apartarLoDeBodega(d.id),
  "piloto-armar-rs": (d) => armarRemisionDesdePedido(d.id),
  "piloto-fv-sel": (d, el, e) => {
    if (e && e.type === "input") return;
    const actual = (state.pilotoFV && state.pilotoFV.opId === d.op) ? state.pilotoFV.sel : {};
    state.pilotoFV = { opId: d.op, sel: { ...actual, [d.rc]: !actual[d.rc] } };
    render();
  },
  "piloto-facturar": (d) => {
    const sel = (state.pilotoFV && state.pilotoFV.opId === d.id) ? state.pilotoFV.sel : {};
    const ids = Object.keys(sel).filter(k => sel[k]);
    if (!ids.length) return;
    // La MISMA puerta del panel de remisiones sin facturar.
    const plan = planDesdeRemisiones(ids);
    if (!plan) { alert("⚠️ No se pudo armar la factura con esas remisiones."); return; }
    if (plan.error) { alert("⚠️ " + plan.error); return; }
    state.pilotoFV = null;
    showFacturarPlan(plan);
  },
  "piloto-liberar": (d) => {
    const a = (state.data.apartados || []).find(x => x.id === d.id);
    if (!a) return;
    const motivo = (window.prompt(`¿Por qué se libera el apartado de ${a.articulo} × ${a.cantidad} (${a.pedido_numero || ""})?`) || "").trim();
    if (!motivo) return;
    saveData("apartados", state.data.apartados.map(x => x.id === a.id ? { ...x, liberado: { fecha: today(), por: state.usuarioNombre || state.usuarioEmail || null, motivo } } : x));
    render();
  }
});
